/**
 * /well-servicing/jobs/:id — one job (GET / PATCH / DELETE
 * /well-servicing/jobs/:id, fieldiq-api#1282, WS-2).
 *
 * A full page with tabs (`?tab=`, so Back and a shared link keep the place):
 * Overview (details, edit), BOP stack, Equipment, Rent-ins, Tour sheets, and
 * (WS-3, fieldiq-api#1286) String, Tallies and the end-of-job Report.
 * Forms open inline, never as a modal; a status move, a delete and a removal
 * ask in-app first.
 *
 * THE STATUS BAR SHOWS ONLY THE MOVES THE API ALLOWS. The buttons are the job's
 * own allowedTransitions (the API's WS_JOB_TRANSITIONS from where it stands),
 * shown to the dispatch roles only — nothing here keeps a second copy of the
 * state machine. Cancelling asks for a reason (the API requires one). A move
 * the API still refuses reads as the refusal sentence (invalid_transition
 * naming where it can go, job_has_draft_tours naming the day).
 *
 * Delete is offered on a draft or scheduled job; a job with sheets is refused
 * (job_has_tours) and the refusal says to cancel instead. Rent-in cost is shown
 * only to the roles the API sends it to.
 */
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Activity, ClipboardList, Pencil, Trash2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { canUseWsAi } from '../../lib/ws-ai';
import { WsAiLauncher } from './ai/WsAiBits';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useWsEquipmentOptions, useWsMeta, useWsUsers } from '../../hooks/useWellServicing';
import { useDeleteWsJob, useUpdateWsJob, useWsJob } from '../../hooks/useWsJobs';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canDispatchWs,
  canSeeWsRentInCost,
  canWriteWsTours,
  catalogLabel,
  wellIdLabel,
  wsDay,
  wsJobDeletable,
  wsJobOpen,
  wsJobRefusal,
  wsJobStatusSkin,
  wsJobTourable,
} from '../../lib/well-servicing';
import { useBackToList, useOpenRecord } from '../downhole/recordPage';
import { plannedRange, scopeText } from './JobBits';
import { JobForm } from './JobForm';
import { BopStackSection, EquipmentSection, RentInsSection, ToursSection } from './JobSections';
import { ReportsSection } from './ReportsSection';
import { canReadWsTickets, canWriteWsTickets } from '../../lib/ws-tickets';
import { WsJobTicketsSection, WsRentInBillingPanel } from './tickets/JobTicketSections';
import { WsJobInvoicesSection } from './invoices/InvoiceBits';
import { StringSection, TalliesSection } from './StringSections';
import {
  isNotFound,
  WS_BAND_SECONDARY,
  WS_EYEBROW,
  WS_INPUT,
  WS_PRIMARY,
  WS_SECONDARY,
  WS_SOUR_SKIN,
  WsBadge,
  WsDialog,
  WsEmpty,
  WsField,
  WsFormError,
  WsLoading,
  WsPanel,
  WsSpec,
} from './WsBits';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'bop', label: 'BOP stack' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'rent-ins', label: 'Rent-ins' },
  { key: 'tours', label: 'Tour sheets' },
  { key: 'string', label: 'String' },
  { key: 'tallies', label: 'Tallies' },
  { key: 'report', label: 'Report' },
  { key: 'tickets', label: 'Tickets' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** The button for a move, by where it goes. */
export const MOVE_LABEL: Record<string, string> = {
  draft: 'Back to draft',
  scheduled: 'Schedule',
  moving_in: 'Start moving in',
  in_progress: 'Start work',
  rigging_out: 'Start rigging out',
  completed: 'Complete',
  cancelled: 'Cancel job',
};
const MOVE_BODY: Record<string, string> = {
  draft: 'Moves the job back to draft. A tour sheet cannot be opened on a draft.',
  scheduled: 'Schedules the job. The crew can open tour sheets on it from today.',
  moving_in: 'Marks the rig moving in and rigging up. The first tour sheet does this on its own.',
  in_progress: 'Marks the rig up and working on the well.',
  rigging_out: 'Marks the rig rigging out.',
  completed: 'Completes the job. Every tour sheet must be submitted first. A completed job takes no more changes.',
  cancelled: 'Cancels the job. A cancelled job takes no more tour sheets or changes.',
};

export default function WsJobDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const back = useBackToList('/well-servicing/jobs');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const tab: TabKey = TABS.some((t) => t.key === tabParam) ? (tabParam as TabKey) : 'overview';
  const pickTab = (key: TabKey) => {
    const p = new URLSearchParams(searchParams);
    if (key === 'overview') p.delete('tab');
    else p.set('tab', key);
    setSearchParams(p, { replace: true });
  };

  const { user } = useAuth();
  const role = user?.role;
  const canDispatch = canDispatchWs(role);
  const query = useWsJob(id);
  const catalog = useWsMeta().data?.data ?? null;
  const users = useWsUsers(canDispatch);
  const equipment = useWsEquipmentOptions(canDispatch);
  const update = useUpdateWsJob();
  const del = useDeleteWsJob();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [move, setMove] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const job = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const open = !!job && wsJobOpen(job.status);
  const canWrite = canDispatch && open;
  const moves = canDispatch && job ? job.allowedTransitions ?? [] : [];
  const forward = moves.find((m) => m !== 'cancelled' && m !== 'draft');

  const doMove = () => {
    if (!job || !move) return;
    setMoveError(null);
    if (move === 'cancelled' && !cancelReason.trim()) return setMoveError('Say why the job is cancelled.');
    update.mutate(
      { id: job.id, body: move === 'cancelled' ? { status: move, cancel_reason: cancelReason.trim() } : { status: move } },
      {
        onSuccess: () => {
          toast.success(`Job moved to ${wsJobStatusSkin(move).label.toLowerCase()}`);
          setMove(null);
          setCancelReason('');
        },
        onError: (err) => setMoveError(wsJobRefusal(err, 'The status could not be changed.')),
      },
    );
  };

  const counts: Partial<Record<TabKey, number>> = job
    ? { equipment: job.equipment.length, 'rent-ins': job.rentIns.length, tours: job.tourCount }
    : {};

  return (
    <div data-testid="ws-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title={job?.jobNumber || (query.isLoading ? 'Job' : '—')}
        description={job ? `${job.customerName ?? '—'} · ${job.wellName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          job && canDispatch ? (
            <div className="flex flex-wrap gap-2" data-testid="ws-job-actions">
              {moves.map((to) => (
                <button
                  key={to}
                  type="button"
                  data-move={to}
                  data-testid={`ws-move-${to}`}
                  className={to === forward ? 'fi-action fi-action--standard fi-action--primary' : WS_BAND_SECONDARY}
                  onClick={() => { setMoveError(null); setCancelReason(''); setMove(to); }}
                >
                  {MOVE_LABEL[to] ?? wsJobStatusSkin(to).label}
                </button>
              ))}
              {open && (
                <button type="button" className={WS_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); pickTab('overview'); }} disabled={editing}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit
                </button>
              )}
              {wsJobDeletable(job.status) && (
                <button type="button" className={WS_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Status', tone: 'neutral', loading: query.isLoading, value: job ? wsJobStatusSkin(job.status, job.statusLabel).label : '—' },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Rig', tone: 'neutral', loading: query.isLoading, value: job?.rigNumber || '—' },
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Tour sheets', sub: job?.lastTourDate ? `Last ${wsDay(job.lastTourDate)}` : undefined, tone: 'neutral', loading: query.isLoading, value: job ? displayValue(job.tourCount ?? undefined) : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WsLoading label="Loading job" />
          ) : notFound || (!failure && !job) ? (
            <WsPanel eyebrow="Job" title="Job">
              <WsEmpty>This job was not found. It may have been deleted, or you may not be on its crew.</WsEmpty>
            </WsPanel>
          ) : failure || !job ? (
            <WsPanel eyebrow="Job" title="Job">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this job" />
            </WsPanel>
          ) : (
            <>
              <div role="tablist" aria-label="Job sections" className="flex gap-5 overflow-x-auto border-b border-fieldiq-border">
                {TABS.filter((t) => t.key !== 'tickets' || canReadWsTickets(role)).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    onClick={() => pickTab(t.key)}
                    className={`whitespace-nowrap border-b-2 px-1 pb-2 text-sm transition ${
                      tab === t.key ? 'border-fi-amber font-extrabold text-fieldiq-text' : 'border-transparent font-bold text-fieldiq-muted hover:text-fieldiq-text'
                    }`}
                  >
                    {t.label}
                    {counts[t.key] !== undefined ? ` (${counts[t.key]})` : ''}
                  </button>
                ))}
              </div>

              {tab === 'overview' && (
                <>
                  {!open && (
                    <p className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="ws-job-closed">
                      This job is {wsJobStatusSkin(job.status, job.statusLabel).label.toLowerCase()}. It takes no more tour sheets or changes.
                      {job.cancelReason ? ` Reason: ${job.cancelReason}` : ''}
                    </p>
                  )}
                  <WsPanel eyebrow="Job" title="Details" testId="ws-job-details">
                    <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                      <WsSpec label="Status" value={<WsBadge skin={wsJobStatusSkin(job.status, job.statusLabel)} value={job.status} />} />
                      <WsSpec label="Customer" value={job.customerName || '—'} />
                      <WsSpec
                        label="Well"
                        value={
                          <button type="button" className="text-left font-bold text-fieldiq-text underline" onClick={() => openRecord(`/well-servicing/wells/${job.wellId}`)}>
                            {job.wellName || '—'}
                          </button>
                        }
                      />
                      <WsSpec label={wellIdLabel(meta)} value={job.wellUwi || job.wellApiNumber || '—'} mono />
                      <WsSpec label="Field" value={job.wellFieldName || '—'} />
                      <WsSpec label="Service" value={job.wellSour ? <WsBadge skin={WS_SOUR_SKIN} value="sour" /> : job.wellSour === false ? 'Sweet' : '—'} />
                      <WsSpec
                        label="Rig"
                        value={
                          job.rigId ? (
                            <button type="button" className="text-left font-bold text-fieldiq-text underline" onClick={() => openRecord(`/well-servicing/rigs/${job.rigId}`)}>
                              {job.rigNumber || '—'}
                            </button>
                          ) : (
                            '—'
                          )
                        }
                      />
                      <WsSpec label="BOP stack" value={job.bopStackName || '—'} />
                      <WsSpec label="Scope" value={scopeText(job)} />
                      <WsSpec label="Tickets" value={catalogLabel(catalog?.ticketModes, job.ticketMode)} />
                      <WsSpec label="Supervisor" value={job.supervisorName || '—'} />
                      <WsSpec label="Planned" value={plannedRange(job.plannedStartOn, job.plannedEndOn)} />
                      <WsSpec label="PO number" value={job.poNumber || '—'} mono />
                      <WsSpec label="AFE number" value={job.afeNumber || '—'} mono />
                      <WsSpec label="Cost code" value={job.costCode || '—'} mono />
                      <WsSpec label="Currency" value={job.currency || '—'} />
                    </dl>
                    {job.description && (
                      <p data-testid="ws-job-description" className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                        {job.description}
                      </p>
                    )}
                    {job.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{job.notes}</p>}
                  </WsPanel>

                  {editing && canWrite && (
                    <WsPanel eyebrow="Edit" title="Edit job" testId="ws-job-edit">
                      <JobForm
                        job={job}
                        catalog={catalog}
                        meta={meta}
                        users={users.data ?? []}
                        pending={update.isPending}
                        error={editError}
                        submitLabel="Save job"
                        onCancel={() => setEditing(false)}
                        onSubmit={(body) =>
                          update.mutate(
                            { id: job.id, body },
                            {
                              onSuccess: () => {
                                toast.success('Job saved');
                                setEditing(false);
                              },
                              onError: (err) => setEditError(wsJobRefusal(err, 'The job could not be saved.')),
                            },
                          )
                        }
                      />
                    </WsPanel>
                  )}
                </>
              )}

              {tab === 'bop' && <BopStackSection job={job} canWrite={canWrite} />}
              {tab === 'equipment' && <EquipmentSection job={job} equipment={equipment.data ?? []} canWrite={canWrite} />}
              {tab === 'rent-ins' && (
                <>
                  <RentInsSection job={job} catalog={catalog} currency={job.currency ?? meta?.currency ?? null} canWrite={canWrite} canSeeCost={canSeeWsRentInCost(role)} />
                  {canReadWsTickets(role) && <WsRentInBillingPanel job={job} rentIns={job.rentIns} canWrite={canWriteWsTickets(role)} />}
                </>
              )}
              {tab === 'tours' && (
                <ToursSection
                  job={job}
                  today={meta?.today ?? null}
                  canOpen={canWriteWsTours(role) && wsJobTourable(job.status) && !!job.rigId}
                  onOpen={(tourId) => openRecord(`/well-servicing/tours/${tourId}`)}
                />
              )}
              {tab === 'string' && <StringSection job={job} catalog={catalog} depthUnit={meta?.units?.depth ?? 'm'} role={role} />}
              {tab === 'tallies' && <TalliesSection job={job} catalog={catalog} depthUnit={meta?.units?.depth ?? 'm'} role={role} />}
              {tab === 'report' && <ReportsSection job={job} meta={meta} canDispatch={canDispatch} canAi={canUseWsAi(role)} />}
              {tab === 'tickets' && canReadWsTickets(role) && (
                <div className="space-y-4">
                  <WsJobTicketsSection job={job} canWrite={canWriteWsTickets(role)} today={meta?.today ?? null} />
                  <WsJobInvoicesSection jobId={job.id} />
                  {canUseWsAi(role) && (
                    <WsAiLauncher
                      request={{ agent: 'ws_ticket_preflight', jobId: job.id }}
                      eyebrow="Before sending"
                      title="Job preflight"
                      blurb="Checks this job's open tickets and approved tour sheets for unpriced lines, sheets on no ticket, and billing classes, subsistence or rent-ins with no rate. The AI's text explains the flags; it changes nothing."
                      testId="ws-ai-job-preflight"
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {job && canDispatch && (
        <>
          <WsDialog
            open={!!move}
            title={move ? MOVE_LABEL[move] ?? 'Change status' : 'Change status'}
            onClose={() => setMove(null)}
            footer={
              <>
                <button type="button" className={WS_SECONDARY} onClick={() => setMove(null)} disabled={update.isPending}>
                  Not now
                </button>
                <button type="button" className={WS_PRIMARY} onClick={doMove} disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : move ? MOVE_LABEL[move] ?? 'Confirm' : 'Confirm'}
                </button>
              </>
            }
          >
            <WsFormError message={moveError} />
            <p className="text-sm text-fieldiq-text">{move ? MOVE_BODY[move] ?? '' : ''}</p>
            {move === 'cancelled' && (
              <div className="mt-3">
                <WsField label="Reason">
                  <textarea className={`${WS_INPUT} h-20 py-2`} aria-label="Cancel reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                </WsField>
              </div>
            )}
          </WsDialog>
          <WsDialog
            open={deleting}
            title="Delete job"
            onClose={() => setDeleting(false)}
            footer={
              <>
                <button type="button" className={WS_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                  Keep job
                </button>
                <button
                  type="button"
                  className={WS_PRIMARY}
                  disabled={del.isPending}
                  onClick={() =>
                    del.mutate(job.id, {
                      onSuccess: () => {
                        toast.success('Job deleted');
                        navigate('/well-servicing/jobs', { replace: true });
                      },
                      onError: (err) => setDeleteError(wsJobRefusal(err, 'The job could not be deleted.')),
                    })
                  }
                >
                  {del.isPending ? 'Deleting…' : 'Delete job'}
                </button>
              </>
            }
          >
            <WsFormError message={deleteError} />
            <p className="text-sm text-fieldiq-text">Removes {job.jobNumber} from the register. A job with tour sheets cannot be deleted — cancel it instead.</p>
          </WsDialog>
        </>
      )}
    </div>
  );
}
